import { mdiAutoFix, mdiClose, mdiFilePdfBox, mdiFilePowerpointOutline, mdiFileWordOutline } from '@mdi/js'
import Icon from '@mdi/react'
import PhotoIcon from '@mui/icons-material/InsertPhoto'
import IconSearch from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import { ACTION_RESET_CONTEXT_FILES, ComposerMediaContext } from 'components/ComposerMediaContext'
import { ConnectedFileInput } from 'components/FileHelpers/ConnectedFileInput'
import FileUpload from 'components/FileUpload'
import { useComposerVideoUpload } from 'hooks/useComposerVideoUpload'
import { ARTICLE_TYPE, VIDEO_TYPE } from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { firstValueFrom } from 'rxjs'
import {
  addComposerImages,
  addComposerVideo,
  deleteFile,
  removeComposerImages,
  setComposerDocumentUpload,
  videoFileAdded
} from 'services/compose'
import {
  composerAllLinksSelector,
  composerContentItemSelector,
  composerDocumentUploadSelector,
  composerImagesSelector,
  composerImageUrlsSelector,
  composerInstagramCoverUrlSelector,
  composerIsUploadingSelector,
  composerTabsSelector,
  composerVideoThumbnailSelector,
  composerVideoUrlSelector,
  composerWithImagesSelector,
  instagramPostSelector
} from 'services/compose/selectors'
import { checkFeatureAvailability } from 'services/product'
import { message } from 'services/snackbar'
import { openComposerContentDialog, openVideoCoverDialog } from 'services/ui'
import {
  ALLOWED_VIDEO_FORMATS,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  FEATURE_POST_MULTI_PHOTO,
  LINKEDIN_DOCUMENT_EXTENSIONS,
  LINKEDIN_DOCUMENT_MIME_TYPES,
  MAX_COMPOSER_PHOTOS_COUNT_FACEBOOK,
  MAX_COMPOSER_PHOTOS_COUNT_TWITTER,
  MAX_LINKEDIN_DOCUMENT_SIZE_BYTES,
  POST_TYPE_STORY,
  STOCK_VIDEO_TYPE
} from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { ALLOWED_PHOTO_FORMATS, INPUT_ACCEPT_PHOTO, INPUT_ACCEPT_VIDEO } from 'utils/file'
import { noop } from 'utils/noop'
import { ComposerContentPreview } from './ComposerContentPreview'
import { joinLabels, storiesNoCoverMessage } from './coverMessages'
import docUploadStyles from './DocumentUpload.pcss'
import styles from './Media.pcss'
import MediaPostOptions from './MediaPostOptions'
import MediaPreview from './MediaPreview'

export function Media() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { dispatch: contextDispatch } = useContext(ComposerMediaContext)

  const content = useSelector(composerContentItemSelector)
  const links = useSelector(composerAllLinksSelector)
  const videoThumbnail = useSelector(composerVideoThumbnailSelector)
  const videoUrl = useSelector(composerVideoUrlSelector)
  const igCoverUrl = useSelector(composerInstagramCoverUrlSelector)
  // EXPL: A thumbnail URL can point at an image the storage never generated (e.g. the poster frame
  // derived from a video url), so keep the failing one out of the DOM instead of showing it broken.
  const [brokenThumbnail, setBrokenThumbnail] = useState<string | undefined>(undefined)
  const instagramPost = useSelector(instagramPostSelector)
  const imageUrls = useSelector(composerImageUrlsSelector)
  const withImages = useSelector(composerWithImagesSelector)
  const networks = useSelector(composerTabsSelector)
  const composerDocument = useSelector(composerDocumentUploadSelector)
  const images = useSelector(composerImagesSelector)
  const videoUpload = useComposerVideoUpload()

  const isUploading = useSelector(composerIsUploadingSelector)
  const withFileUploadError = Object.values(images).find(f => Boolean(f.error)) !== undefined
  const [docFile, setDocFile] = useState<File | null>(null)

  useEffect(() => {
    if (!composerDocument) {
      setDocFile(null)
    }
  }, [composerDocument])

  const onRemoveDocFile = () => {
    setDocFile(null)
  }

  useEffect(() => {
    if (content?.type === VIDEO_TYPE || content?.type === ARTICLE_TYPE || content?.type === STOCK_VIDEO_TYPE) {
      contextDispatch({ type: ACTION_RESET_CONTEXT_FILES })
    }
  }, [content, contextDispatch])

  const hints = useMemo(() => {
    const hintsArray: string[] = []
    if (withFileUploadError) {
      hintsArray.push('composer.hints.generic.file-upload-failed')
    }
    if (isUploading) {
      hintsArray.push('composer.labels.uploading-files')
    }
    return hintsArray
  }, [isUploading, withFileUploadError])

  // EXPL: Feature check - multi-photo posts
  useEffect(() => {
    const imageIds = Object.keys(images)
    if (imageIds.length > 1) {
      dispatch(checkFeatureAvailability(FEATURE_POST_MULTI_PHOTO, false, () => {
        contextDispatch({ type: ACTION_RESET_CONTEXT_FILES })
        // Keep only the first image
        dispatch(removeComposerImages({ ids: imageIds.slice(1), contextDispatch }))
      }))
    }
  }, [dispatch, contextDispatch, images])

  const onFilesSelected = (selection: { [name: string]: File }) => {
    const filesTotalCount = Object.keys(selection).length
    if (filesTotalCount > 0) {
      const photos: File[] = []
      const videos: File[] = []
      const docs: File[] = []

      Object.values(selection).forEach(file => {
        if (ALLOWED_PHOTO_FORMATS.includes(file.type)) {
          photos.push(file)
        } else if (ALLOWED_VIDEO_FORMATS.includes(file.type)) {
          videos.push(file)
        } else if (LINKEDIN_DOCUMENT_MIME_TYPES.includes(file.type)) {
          docs.push(file)
        }
      })

      if (docs.length > 0) {
        if (photos.length + videos.length > 0) {
          dispatch(message('Cannot share a document and other files in the same post.', 'warning'))
        } else if (docs.length > 1) {
          dispatch(message('You can only share one document in a post.', 'warning'))
          return
        } else if (docs[0].size > MAX_LINKEDIN_DOCUMENT_SIZE_BYTES) {
          dispatch(message('The file size exceeds the limit. Please select a file smaller than 100MB.', 'error'))
          return
        } else {
          setDocFile(docs[0])
          return
        }
      }

      if (photos.length + videos.length === 0) {
        return
      }

      if (photos.length + videos.length + docs.length < filesTotalCount) {
        dispatch(message(intl.formatMessage({ id: 'composer.notifications.dropped-some-invalid-files' }), 'warning'))
      }

      if (networks.includes(BRAND_TWITTER) && photos.length > MAX_COMPOSER_PHOTOS_COUNT_TWITTER) {
        dispatch(message(intl.formatMessage(
          { id: 'composer.notifications.tw-photos-count-limit-hit' },
          { count: MAX_COMPOSER_PHOTOS_COUNT_TWITTER }
        ), 'warning'))
      }

      if (networks.includes(BRAND_FACEBOOK) && photos.length > MAX_COMPOSER_PHOTOS_COUNT_FACEBOOK) {
        dispatch(message(intl.formatMessage(
          { id: 'composer.notifications.fb-photos-count-limit-hit' },
          { count: MAX_COMPOSER_PHOTOS_COUNT_FACEBOOK }
        ), 'warning'))
      }

      if (photos.length > 0 && videos.length > 0) {
        dispatch(message(intl.formatMessage({ id: 'composer.notifications.images-and-video' }), 'warning'))
        return
      }

      if (photos.length > 0) {
        dispatch(addComposerImages({ items: photos.map(photo => ({ file: photo })), contextDispatch }))
      } else if (videos.length > 1) {
        dispatch(message(intl.formatMessage({ id: 'composer.notifications.one-video-only' }), 'warning'))
      } else if (videos.length === 1) {
        dispatch(removeComposerImages({ ids: Object.keys(images), contextDispatch }))
        contextDispatch({ type: ACTION_RESET_CONTEXT_FILES })
        dispatch(addComposerVideo({ video: { file: videos[0] }, contextDispatch }))
        dispatch(videoFileAdded(videos[0]))
      }
    }
  }

  const openGenerateDialog = () => {
    dispatch(openComposerContentDialog({ tab: 'generate' }))
  }

  const openFindDialog = () => {
    dispatch(openComposerContentDialog({ tab: 'search' }))
  }

  const openCoverDialog = () => {
    dispatch(openVideoCoverDialog({ tab: videoUpload?.file ? 'video' : 'upload' }))
  }

  const fileFormats = [...INPUT_ACCEPT_PHOTO, ...INPUT_ACCEPT_VIDEO, ...LINKEDIN_DOCUMENT_EXTENSIONS].join(',')
  const isUpload = Boolean(videoUpload?.file)
  const withVideo = isUpload || Boolean(videoUrl)

  // EXPL: Stories are published without a cover image, so a network posting a story doesn't count towards the picker
  const igIsStory = networks.includes(BRAND_INSTAGRAM) && instagramPost.postType === POST_TYPE_STORY
  const igEligible = networks.includes(BRAND_INSTAGRAM) && !igIsStory
  const coverPreview = igEligible ? igCoverUrl : undefined
  const thumbnailPreview = coverPreview || videoThumbnail

  // EXPL: Only Instagram and TikTok accept a picked thumbnail - other networks (incl. Facebook) select one automatically
  const coverNetworks = useMemo(() => [
    igEligible ? 'Instagram' : '',
    networks.includes(BRAND_TIKTOK) ? 'TikTok' : ''
  ].filter(Boolean), [networks, igEligible])

  const storyMessage = igIsStory ? storiesNoCoverMessage(['Instagram']) : ''

  const thumbnailDisabledReason = useMemo(() => {
    if (coverNetworks.length === 0) {
      return storyMessage || 'Thumbnails are only supported for TikTok and Instagram.'
    }
    // EXPL: A URL-sourced video can't be scrubbed for a frame, but Instagram still accepts a custom cover upload
    if (!isUpload && !igEligible) {
      return 'Thumbnails can only be picked for videos uploaded from your device.'
    }
    return ''
  }, [isUpload, igEligible, coverNetworks, storyMessage])

  const thumbnailMessage = useMemo(() => (isUpload
    ? `Choose a video thumbnail for ${joinLabels(coverNetworks)}. Other networks will select one automatically.`
    : 'Upload a custom cover image for Instagram. Videos added by URL do not support picking a frame from the video.'
  ), [isUpload, coverNetworks])

  return (
    <div data-testid="composer-media">
      {(docFile || composerDocument) && <DocumentUpload file={docFile} onRemove={onRemoveDocFile} />}
      <div className={styles['btn-box']}>
        <ConnectedFileInput
          accept={fileFormats}
          className={styles.action}
          disabled={Boolean(docFile)}
          openFileDialog={noop}
          setUploaderFiles={onFilesSelected}
        >
          <Button startIcon={<PhotoIcon />} disabled={Boolean(docFile)} className={styles['file-btn']}>
            <FormattedMessage id="composer.labels.upload-files" />
          </Button>
        </ConnectedFileInput>
        <Button
          startIcon={<Icon path={mdiAutoFix} size="20px" />}
          className={styles.action}
          onClick={openGenerateDialog}
        >
          Generate
        </Button>
        <Button startIcon={<IconSearch />} className={styles.action} onClick={openFindDialog}>
          Find
        </Button>
      </div>
      <div className={styles['uploader-box']}>
        <MediaPreview className={styles['preview-box']} />
        {withVideo && (
          <Fragment>
            {!thumbnailDisabledReason && (
              <div className={styles['thumb-title']}>
                {thumbnailMessage}
              </div>
            )}
            <div className={styles['thumbnail-box']}>
              {thumbnailPreview && thumbnailPreview !== brokenThumbnail && (
                <img src={thumbnailPreview} onError={() => setBrokenThumbnail(thumbnailPreview)} />
              )}
              {coverPreview && coverPreview !== brokenThumbnail && (
                <span className={styles['thumb-caption']}>Instagram cover</span>
              )}
              <Button disabled={isUploading === 'video' || Boolean(thumbnailDisabledReason)} onClick={openCoverDialog}>
                Cover / thumbnail
              </Button>
              {(thumbnailDisabledReason || storyMessage) && (
                <span className={styles['thumb-hint']}>{thumbnailDisabledReason || storyMessage}</span>
              )}
            </div>
          </Fragment>
        )}
        <MediaPostOptions />
        {((content && content.type !== TEXT_TYPE) || links?.length > 0) && !withImages && (
          <ComposerContentPreview className={styles['content-preview']} />
        )}
      </div>
      <div className={styles.hints}>
        {hints.map(key => (
          <div key={key}>
            <FormattedMessage id={key} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default Media

export function DocumentUpload({ file, onRemove }: { file: File | null, onRemove: () => void }) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [loading, setLoading] = useState(Boolean(file))
  const [error, setError] = useState<string | null>(null)
  const doc = useSelector(composerDocumentUploadSelector)

  const icon = useMemo(() => {
    const fileExtension = file?.name.substring(file.name.lastIndexOf('.'))
    switch (fileExtension) {
      case '.pdf':
        return mdiFilePdfBox
      case '.doc':
      case '.docx':
        return mdiFileWordOutline
      case '.ppt':
      case '.pptx':
        return mdiFilePowerpointOutline
      default:
        return null
    }
  }, [file?.name])

  const onFileUploaded = (url: string, f: File) => {
    dispatch(setComposerDocumentUpload({ url, name: f.name }))
    setLoading(false)
  }

  const onUploadError = (_: any, e: string) => {
    setError(e)
    setLoading(false)
  }

  const clearFile = () => {
    setError(null)
    setLoading(false)
    dispatch(setComposerDocumentUpload(undefined))
    if (doc) {
      firstValueFrom(dispatch(deleteFile(doc.url)))
    }
    onRemove()
  }

  const fileName = file?.name || doc?.name || ''

  return (
    <div className={docUploadStyles['uploader-box']}>
      <div className={docUploadStyles['file-info']}>
        <Tooltip title="Remove document">
          <span className={docUploadStyles['btn-remove-file']} data-testid="btn-remove-doc" onClick={clearFile}>
            <Icon path={mdiClose} size="16px" />
          </span>
        </Tooltip>
        {loading ? <div className={docUploadStyles.loader}><CircularProgress size={16} color="primary" /></div>
          : icon && <Icon className={docUploadStyles['icon-doc']} path={icon} size="24px" />}
        <span className={`${docUploadStyles.filename} text-ellipsis`}>{fileName}</span>
      </div>
      {error && <p className={docUploadStyles['error-msg']}>{error}</p>}
      {file && (
        <FileUpload
          file={file}
          abortOnUnmount
          skipValidation
          uploadId={file.name}
          withProgress={false}
          className={docUploadStyles.uploader}
          onFileUploaded={onFileUploaded}
          onError={onUploadError}
        />
      )}
    </div>
  )
}
